<template>
  <a-drawer
    :visible="visible"
    :title="isEditing ? 'Edit Analytics Alert' : 'Add Analytics Alert'"
    placement="right"
    width="90%"
    destroy-on-close
    @close="$emit('close')"
  >
    <a-form layout="vertical">
      <a-form-item label="Alert Name">
        <a-input
          v-model:value="form.name"
          placeholder="e.g. Weekly line 3 summary"
          style="max-width: 420px"
        />
      </a-form-item>

      <a-divider orientation="left">Report Parameters</a-divider>

      <a-alert
        type="info"
        show-icon
        class="mb-3"
        :message="`The date range below is only used to preview this configuration. Each report covers the last ${lookback} day(s), ending when it is sent.`"
      />

      <Filters
        ref="filters"
        :show-cycle-input="false"
        :show-duration-input="true"
        :show-steps-filter="false"
        :is-no-data-available="false"
        :is-analytics="true"
        @change="handlePreviewCount"
      />

      <div class="mt-2 mb-3">
        <a-spin v-if="isCountingCycles" size="small" />
        <span v-else-if="previewMessage" class="text-muted">
          {{ previewMessage }}
        </span>
        <span v-else class="text-muted">
          Press <strong>Update</strong> above to check how many cycles this
          configuration currently matches.
        </span>
      </div>

      <a-divider orientation="left">Schedule</a-divider>

      <a-row :gutter="16">
        <a-col :xs="24" :sm="12" :md="6">
          <a-form-item label="Frequency">
            <a-select
              v-model:value="form.frequency"
              :options="frequencies"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>

        <a-col v-if="form.frequency === 'weekly'" :xs="24" :sm="12" :md="6">
          <a-form-item label="Day of Week">
            <a-select
              v-model:value="form.day_of_week"
              :options="daysOfWeek"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>

        <a-col v-if="form.frequency === 'monthly'" :xs="24" :sm="12" :md="6">
          <a-form-item label="Day of Month">
            <a-select
              v-model:value="form.day_of_month"
              :options="daysOfMonth"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>

        <a-col :xs="24" :sm="12" :md="6">
          <a-form-item label="Send Time">
            <a-time-picker
              v-model:value="sendTime"
              format="HH:mm"
              value-format="HH:mm"
              :allow-clear="false"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>

        <a-col :xs="24" :sm="12" :md="6">
          <a-form-item label="Timezone">
            <a-select
              v-model:value="form.timezone"
              show-search
              :options="timezones"
              :filter-option="filterTimezone"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <p class="text-muted">Report window: last {{ lookback }} day(s).</p>

      <a-divider orientation="left">Recipients</a-divider>

      <a-form-item>
        <div class="recipients-list mb-2">
          <!-- Edit mode: recipients saved on server -->
          <template v-if="isEditing">
            <a-tag
              v-for="r in config.recipients"
              :key="r.id"
              closable
              style="margin-bottom: 6px"
              @close="$emit('delete-recipient', config.id, r.id)"
            >
              {{ r.email }}
            </a-tag>
            <span
              v-if="!config.recipients || config.recipients.length === 0"
              class="text-muted"
            >
              No recipients yet
            </span>
          </template>
          <!-- Add mode: pending emails (submitted after the config is created) -->
          <template v-else>
            <a-tag
              v-for="(email, idx) in pendingRecipients"
              :key="idx"
              closable
              style="margin-bottom: 6px"
              @close="pendingRecipients.splice(idx, 1)"
            >
              {{ email }}
            </a-tag>
            <span v-if="pendingRecipients.length === 0" class="text-muted">
              No recipients added yet
            </span>
          </template>
        </div>
        <div class="d-flex" style="gap: 8px; max-width: 420px">
          <a-input
            v-model:value="newRecipientEmail"
            placeholder="Email address"
            style="flex: 1"
            @keydown.enter="handleAddRecipient"
          />
          <a-button
            type="primary"
            :loading="isAddingRecipient"
            @click="handleAddRecipient"
          >
            Add
          </a-button>
        </div>
      </a-form-item>

      <a-form-item>
        <a-space>
          <a-switch v-model:checked="form.is_active" size="small" />
          <span>Active</span>
        </a-space>
      </a-form-item>
    </a-form>

    <template #extra>
      <a-space>
        <a-button @click="$emit('close')">Cancel</a-button>
        <a-popconfirm
          v-if="isEditing"
          :title="sendNowTitle"
          ok-text="Send"
          cancel-text="Cancel"
          @confirm="$emit('send-now', config)"
        >
          <a-button :disabled="!hasRecipients">Send Now</a-button>
        </a-popconfirm>
        <a-button type="primary" :loading="isSubmitting" @click="handleSave">
          Save
        </a-button>
      </a-space>
    </template>
  </a-drawer>
</template>

<script>
import populateFilters from 'src/components/shared/Helpers/populateFilters';
import Filters from 'src/components/shared/Components/AdvanceFilters';
import TelemetryService from 'src/services/telemetry';
import { mapActions, mapGetters } from 'vuex';
import {
  DAYS_OF_MONTH,
  DAYS_OF_WEEK,
  FREQUENCIES,
  TIMEZONE_OPTIONS,
  filtersToQuery,
  getBrowserTimezone,
  lookbackDays,
  toStoredFilters,
} from './schedule';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaultForm = () => ({
  name: '',
  frequency: 'weekly',
  day_of_week: 0,
  day_of_month: 1,
  send_time: '06:00',
  timezone: getBrowserTimezone(),
  is_active: true,
});

export default {
  name: 'AnalyticsAlertEditor',
  components: { Filters },
  inject: ['toast'],

  props: {
    visible: { type: Boolean, required: true, default: false },
    // null in add mode, the alert config object in edit mode
    config: { type: Object, required: false, default: null },
    isSubmitting: { type: Boolean, required: true, default: false },
  },

  emits: ['close', 'save', 'send-now', 'delete-recipient', 'add-recipient'],

  data() {
    return {
      form: defaultForm(),
      pendingRecipients: [],
      newRecipientEmail: '',
      isAddingRecipient: false,
      isCountingCycles: false,
      previewMessage: '',
      frequencies: FREQUENCIES,
      daysOfWeek: DAYS_OF_WEEK,
      daysOfMonth: DAYS_OF_MONTH,
      timezones: TIMEZONE_OPTIONS,
    };
  },

  computed: {
    ...mapGetters(['organization', 'devices']),

    isEditing() {
      return !!this.config;
    },

    lookback() {
      return lookbackDays(this.form.frequency);
    },

    hasRecipients() {
      return !!this.config?.recipients?.length;
    },

    sendNowTitle() {
      const count = this.config?.recipients?.length || 0;
      return `Send this report to ${count} recipient(s) now?`;
    },

    // a-time-picker with value-format still wants a string in/out, but binding
    // straight to form.send_time keeps the payload flat.
    sendTime: {
      get() {
        return this.form.send_time;
      },
      set(value) {
        this.form.send_time = value || '06:00';
      },
    },
  },

  watch: {
    visible(isVisible) {
      if (isVisible) this.initialise();
    },
  },

  methods: {
    ...mapActions(['getOrgDevices']),

    async initialise() {
      this.pendingRecipients = [];
      this.newRecipientEmail = '';
      this.previewMessage = '';

      if (!this.isEditing) {
        this.form = defaultForm();
        return;
      }

      this.form = {
        name: this.config.name || '',
        frequency: this.config.frequency || 'weekly',
        day_of_week: this.config.day_of_week ?? 0,
        day_of_month: this.config.day_of_month ?? 1,
        send_time: (this.config.send_time || '06:00').slice(0, 5),
        timezone: this.config.timezone || getBrowserTimezone(),
        is_active: this.config.is_active ?? true,
      };

      await this.hydrateFilters();
    },

    /**
     * Re-applies a saved filter blob to AdvanceFilters by writing it into the
     * route query and running the shared populateFilters helper. AdvanceFilters
     * only auto-calls that helper from a non-immediate `devices` watcher, which
     * will not fire when devices are already cached, so we invoke it directly.
     */
    async hydrateFilters() {
      const query = filtersToQuery(this.config.filters, this.form.frequency);
      if (!query) {
        this.toast.error('This alert has no saved operation or devices');
        return;
      }

      // populateFilters maps serial numbers through deviceSerialNumToDisplayNameMap.
      if (!this.devices || this.devices.length === 0) {
        await this.getOrgDevices(this.organization);
      }

      await this.$router.replace({ name: this.$route.name, query });
      await this.$nextTick();

      const filtersComponent = this.$refs.filters;
      if (!filtersComponent) return;
      await populateFilters.apply(filtersComponent);

      // populateFilters writes cycle_type into Vuex (setSelectedCycleType) while
      // getPayload reads the component-local `cycleType`, so it does not round
      // trip on its own. Patch it here rather than in the shared component,
      // which the Trace and Kaizen pages also rely on.
      filtersComponent.cycleType = this.config.filters?.cycle_type ?? null;
    },

    filterTimezone(input, option) {
      return option.label.toLowerCase().includes(input.toLowerCase());
    },

    async handlePreviewCount(filters) {
      this.isCountingCycles = true;
      this.previewMessage = '';
      const [error, data] = await TelemetryService.fetchCyclesDetailsV2(
        false,
        filters
      );
      this.isCountingCycles = false;

      if (error) {
        this.toast.error('Could not check this configuration against cycles');
        return;
      }
      if (!data || data.body === 'No result found') {
        this.previewMessage =
          'This configuration currently matches 0 cycles for the previewed date range.';
        return;
      }
      const count = data.cycle_stats?.number_of_cycles ?? 0;
      this.previewMessage = `This configuration currently matches ${count} cycle(s) for the previewed date range.`;
    },

    handleAddRecipient() {
      const email = this.newRecipientEmail.trim();
      if (!EMAIL_RE.test(email)) {
        this.toast.error('Please enter a valid email address');
        return;
      }

      if (this.isEditing) {
        const existing = this.config.recipients || [];
        if (existing.some((r) => r.email === email)) {
          this.toast.error('Email already added');
          return;
        }
        this.newRecipientEmail = '';
        this.$emit('add-recipient', this.config.id, email);
        return;
      }

      if (this.pendingRecipients.includes(email)) {
        this.toast.error('Email already added');
        return;
      }
      this.pendingRecipients.push(email);
      this.newRecipientEmail = '';
    },

    handleSave() {
      if (!this.form.name.trim()) {
        this.toast.error('Please enter a name for this alert');
        return;
      }

      // getPayload validates the filter bar and toasts the specific reason,
      // returning false when anything is missing.
      const payload = this.$refs.filters?.getPayload();
      if (!payload) return;

      const filters = toStoredFilters(payload);
      const body = {
        name: this.form.name.trim(),
        task: filters.task,
        filters,
        frequency: this.form.frequency,
        day_of_week:
          this.form.frequency === 'weekly' ? this.form.day_of_week : null,
        day_of_month:
          this.form.frequency === 'monthly' ? this.form.day_of_month : null,
        send_time: this.form.send_time,
        timezone: this.form.timezone,
        is_active: this.form.is_active,
      };

      this.$emit('save', body, this.pendingRecipients);
    },
  },
};
</script>

<style scoped>
.recipients-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
